import React, {useEffect, useState, useRef, createContext, useContext} from "react";
import {BrowserRouter as Router, Routes, Route, Navigate} from "react-router-dom";

import './App.css';

// Layout Components
import Header from "./Components/Header/Header";
import Footer from "./Components/Footer/Footer";
import LoadingSpinner from "./Components/Common/LoadingSpinner";
import ScrollToTop from "./Components/Common/ScrollToTop";
import NotificationToast from "./Components/Common/NotificationToast";


// Public Pages
import Home from "./Pages/Home/Home";
import PropertyListings from "./Pages/PropertyListings/PropertyListings";
import PropertyDetail from "./Pages/PropertyDetail/PropertyDetail";
import SearchResults from "./Pages/SearchResults/SearchResults";
import MapView from "./Pages/MapView/MapView";
import Contact from "./Pages/Contact/Contact";
import About from "./Pages/About/About";

// Authentication Pages
import Login from "./Pages/Authentication/Login";
import Register from "./Pages/Authentication/Register";
import ForgotPassword from "./Pages/Authentication/ForgotPassword";
import ResetPassword from "./Pages/Authentication/ResetPassword";
import EmailVerification from "./Pages/Authentication/EmailVerification";

// Property Manager Dashboard Pages
import AddListing from "./Pages/Dashboard/Manager/AddListing";
import EditListing from "./Pages/Dashboard/Manager/EditListing";
import ManagerMessages from "./Pages/Dashboard/Manager/ManagerMessages";
import ManagerApplications from "./Pages/Dashboard/Manager/ManagerApplications";
import ManagerAnalytics from "./Pages/Dashboard/Manager/ManagerAnalytics";
import ManagerProfile from "./Pages/Dashboard/Manager/ManagerProfile";
import ManagerSettings from "./Pages/Dashboard/Manager/ManagerSettings";

// Protected Route Component
import ProtectedRoute from "./Components/Auth/ProtectedRoute";
import RoleBasedRoute from "./Components/Auth/RoleBasedRoute";

// Legal Routes
import PrivacyPolicy from "./Pages/Legal/PrivacyPolicy";
import TermsOfService from "./Pages/Legal/TermsOfService";

import RentalGuide from "./Pages/RentalGuide/RentalGuide";
import {API_CONSTANTS} from "./Constants";
import dashboardService from "./services/dashboardService";
import MovingServices from "./Pages/MovingServices/MovingServices";
import PropertyManagement from "./Pages/PropertyManagement/PropertyManagement";
import UserNotifications from "./Pages/Notifications/Notifications";
import Neighborhoods from "./Pages/Neighborhoods/Neighborhoods";

// Landlord Property Management Pages
import PropertyDashboard from "./Pages/LandlordProperty/PropertyDashboard";
import PropertyDetails from "./Pages/LandlordProperty/PropertyDetails";
import PropertyForm from "./Components/LandlordProperty/PropertyForm";

// User Dashboard Pages
import UserDashboard from "./Pages/Dashboard/User/UserDashboard";
import UserProfile from "./Pages/Dashboard/User/UserProfile";
import SavedProperties from "./Pages/Dashboard/User/SavedProperties";
import ApplicationHistory from "./Pages/Dashboard/User/ApplicationHistory";
import UserMessages from "./Pages/Dashboard/User/UserMessages";
import PageNotFound from "./Pages/Utility/PageNotFound";
import PropertyApplication from "./Pages/Application/PropertyApplication";

//Reached Here

/*import FAQ from "./Pages/FAQ/FAQ";
import NeighborhoodDetail from "./Pages/Neighborhoods/NeighborhoodDetail";
import UserProfile from "./Pages/Dashboard/User/UserProfile";

// Admin Dashboard Pages (Super Admin)
import AdminDashboard from "./Pages/Dashboard/Admin/AdminDashboard";
import ManageUsers from "./Pages/Dashboard/Admin/ManageUsers";
import ManageManagers from "./Pages/Dashboard/Admin/ManageManagers";
import SystemAnalytics from "./Pages/Dashboard/Admin/SystemAnalytics";
import PlatformSettings from "./Pages/Dashboard/Admin/PlatformSettings";
import ContentManagement from "./Pages/Dashboard/Admin/ContentManagement";
import ReportedContent from "./Pages/Dashboard/Admin/ReportedContent";
import SystemLogs from "./Pages/Dashboard/Admin/SystemLogs";

// Utility Pages
import PageNotFound from "./Pages/Utility/PageNotFound";
import ServerError from "./Pages/Utility/ServerError";
import Maintenance from "./Pages/Utility/Maintenance";
import ComingSoon from "./Pages/Utility/ComingSoon";
*/

// Context Providers
const AuthContext = createContext();
const PropertyContext = createContext();
const NotificationContext = createContext();


// Custom Hooks
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const useProperty = () => useContext(PropertyContext);
export const useNotification = () => useContext(NotificationContext);

function App() {
    // Global State Management
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const userTypesCacheRef = useRef(null);
    const [notifications, setNotifications] = useState([]);
    const [savedProperties, setSavedProperties] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const [userPreferences, setUserPreferences] = useState({
        theme: 'light',
        currency: 'KES',
        language: 'en',
        emailNotifications: true,
        smsNotifications: false
    });

    // App Configuration
    const [appConfig, setAppConfig] = useState({
        maintenanceMode: false,
        featuresEnabled: {
            applications: true,
            messaging: true,
            virtualTours: true,
            paymentIntegration: true,
            backgroundChecks: true
        }
    });

    // Initialize App
    useEffect(() => {
        initializeApp();
    }, []);

    const initializeApp = async () => {
        try {
            // Check for existing session
            const token = localStorage.getItem('authToken');
            const storedUser = localStorage.getItem('userData');
            if (token && storedUser) {
                // Trust the locally stored session immediately so protected
                // routes (e.g. /notifications) don't bounce the user to
                // /login while the token is (re)validated in the background.
                setUser(JSON.parse(storedUser));
                setIsAuthenticated(true);
                await validateSession(token);
            }

            // Load user preferences
            loadUserPreferences();

            // Load recent searches
            loadRecentSearches();

        } catch (error) {
            console.error('App initialization error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const validateSession = async (token) => {
        try {
            const response = await fetch(API_CONSTANTS.BASE_URL + '/auth/validate-token', {
                method: 'GET',
                headers: {'Authorization': `Bearer ${token}`}
            });
            const data = await response.json();
            if (data.success) {
                await establishSession(data.data.user || data.data);
            } else if (response.status === 401) {
                // Token is genuinely invalid/expired - clear the stale session.
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('userData');
                setUser(null);
                setIsAuthenticated(false);
            }
        } catch (error) {
            // Network/transient error - keep the optimistic session from
            // localStorage rather than forcing the user to log in again.
            console.error('Token validation failed:', error);
        }
    };

    const loadUserPreferences = () => {
        const preferences = localStorage.getItem('userPreferences');
        if (preferences) {
            setUserPreferences(JSON.parse(preferences));
        }
    };

    const loadRecentSearches = () => {
        const searches = localStorage.getItem('recentSearches');
        if (searches) {
            setRecentSearches(JSON.parse(searches));
        }
    };

    // The /auth/* endpoints' `role` field has been observed out of sync with
    // the account's actual `userTypeId` (e.g. a Property Seeker account
    // coming back with role: "admin"). userTypeId reflects what the account
    // actually registered as, so it's the source of truth for which UI a
    // user sees - we resolve it via /user-types/ and use it to correct
    // `role` everywhere the user object enters app state.
    const resolveEffectiveRole = async (rawUser) => {
        if (!rawUser || rawUser.userTypeId == null) {
            return rawUser?.role || 'tenant';
        }

        try {
            if (!userTypesCacheRef.current) {
                const response = await fetch(API_CONSTANTS.BASE_URL + '/user-types/');
                const data = await response.json();
                userTypesCacheRef.current = data.success ? data.data.userTypes : [];
            }

            const match = userTypesCacheRef.current.find(
                (t) => String(t.id) === String(rawUser.userTypeId)
            );
            if (match?.label) {
                const label = match.label.toLowerCase();
                if (label.includes('manager')) return 'manager';
                if (label.includes('admin')) return 'admin';
                return 'tenant';
            }
        } catch (error) {
            console.error('Failed to resolve user type:', error);
        }

        // /user-types/ was unreachable or had no matching entry. Fall back
        // to the known seeker/manager ids rather than trusting `role`,
        // which is the field that was wrong in the first place.
        if (String(rawUser.userTypeId) === '4') return 'tenant';
        if (String(rawUser.userTypeId) === '5') return 'manager';
        return rawUser.role || 'tenant';
    };

    // Single point where a user object is accepted into app state, so login,
    // session restore, and email verification all apply the same role
    // correction and stay in sync with localStorage.
    const establishSession = async (rawUser, tokens = {}) => {
        const role = await resolveEffectiveRole(rawUser);
        const normalizedUser = {...rawUser, role};

        if (tokens.accessToken) localStorage.setItem('authToken', tokens.accessToken);
        if (tokens.refreshToken) localStorage.setItem('refreshToken', tokens.refreshToken);
        localStorage.setItem('userData', JSON.stringify(normalizedUser));

        setUser(normalizedUser);
        setIsAuthenticated(true);
        fetchSavedProperties();
        return normalizedUser;
    };

    // The backend is the only source of truth for what's saved - fetch it
    // fresh on every session rather than trusting a local cache that could
    // belong to a different account or have drifted out of sync.
    const fetchSavedProperties = async () => {
        try {
            const properties = await dashboardService.getSavedProperties();
            setSavedProperties(
                properties
                    .map((p) => Number(p.propertyId ?? p.id))
                    .filter((id) => !Number.isNaN(id))
            );
        } catch (error) {
            console.error('Failed to load saved properties:', error);
        }
    };

    // Auth Functions
    const login = async (credentials) => {
        const response = await fetch(API_CONSTANTS.BASE_URL + '/auth/login', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(credentials)
        });
        const data = await response.json();
        if (response.ok && data.success) {
            const normalizedUser = await establishSession(data.data.user, {
                accessToken: data.data.accessToken,
                refreshToken: data.data.refreshToken
            });
            addNotification('Login successful!', 'success');
            return {...data.data, user: normalizedUser};
        } else {
            // Preserve status/field detail so callers can render specific
            // messaging (invalid credentials vs. disabled account vs.
            // field-level validation) instead of a generic failure.
            const error = new Error(data.error || data.message || 'Login failed');
            error.status = response.status;
            error.field = data.field;
            error.fieldMessage = data.message;
            throw error;
        }
    };

    const logout = async () => {
        try {
            await fetch(API_CONSTANTS.BASE_URL + '/auth/logout', {
                method: 'POST',
                headers: {'Authorization': `Bearer ${localStorage.getItem('authToken')}`}
            });
        } catch (error) {
            console.error('Logout error:', error);
        }
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        setUser(null);
        setIsAuthenticated(false);
        setSavedProperties([]);
        addNotification('Logged out successfully', 'info');
    };

    const register = async (userData) => {
        const response = await fetch(API_CONSTANTS.BASE_URL + '/auth/register', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(userData)
        });
        const data = await response.json();
        if (response.ok && data.success) {
            addNotification('Registration successful! Please check your email to verify your account.', 'success');
            return data.data;
        } else {
            const error = new Error(data.error || data.message || 'Registration failed');
            error.status = response.status;
            error.field = data.field;
            error.fieldMessage = data.message;
            throw error;
        }
    };

    // Property Functions
    const saveProperty = async (propertyId) => {
        if (!isAuthenticated) {
            addNotification('Please log in to save properties', 'error');
            return;
        }
        try {
            await dashboardService.saveProperty({propertyId});
            setSavedProperties((prev) => prev.includes(propertyId) ? prev : [...prev, propertyId]);
            addNotification('Property saved to favorites!', 'success');
        } catch (error) {
            addNotification(error.message || 'Failed to save property', 'error');
        }
    };

    const unsaveProperty = async (propertyId) => {
        try {
            await dashboardService.unsaveProperty(propertyId);
            setSavedProperties((prev) => prev.filter((id) => id !== propertyId));
            addNotification('Property removed from favorites', 'info');
        } catch (error) {
            addNotification(error.message || 'Failed to remove property', 'error');
        }
    };

    const toggleSaveProperty = (propertyId) => {
        return savedProperties.includes(propertyId)
            ? unsaveProperty(propertyId)
            : saveProperty(propertyId);
    };

    const addRecentSearch = (searchQuery) => {
        const newSearches = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 10);
        setRecentSearches(newSearches);
        localStorage.setItem('recentSearches', JSON.stringify(newSearches));
    };

    // Notification Functions
    const addNotification = (message, type = 'info', duration = 5000) => {
        const notification = {
            id: Date.now() + Math.random(),
            message,
            type,
            timestamp: new Date()
        };
        setNotifications(prev => [...prev, notification]);

        // Auto remove notification
        setTimeout(() => {
            removeNotification(notification.id);
        }, duration);
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
    };

    // Context Values
    const authContextValue = {
        user,
        isAuthenticated,
        login,
        logout,
        register,
        establishSession,
        isLoading: isLoading
    };

    const propertyContextValue = {
        savedProperties,
        saveProperty,
        unsaveProperty,
        toggleSaveProperty,
        recentSearches,
        addRecentSearch
    };

    const notificationContextValue = {
        notifications,
        addNotification,
        removeNotification
    };

    /*  // Show maintenance page if enabled
      if (appConfig.maintenanceMode) {
          return <Maintenance/>;
      }*/

    // Show loading spinner during initialization
    if (isLoading) {
        return <LoadingSpinner/>;
    }

    return (
        <AuthContext.Provider value={authContextValue}>
            <PropertyContext.Provider value={propertyContextValue}>
                <NotificationContext.Provider value={notificationContextValue}>
                    <div className="App">
                        <Router>
                            <ScrollToTop/>
                            <Header/>

                            <main className="main-content">
                                <Routes>
                                    {/* Public Routes */}
                                    <Route path="/" element={<Home/>}/>
                                    <Route path="/listings" element={<PropertyListings/>}/>
                                    <Route path="/property/:id" element={<PropertyDetail/>}/>
                                    <Route path="/search" element={<SearchResults/>}/>
                                    <Route path="/map" element={<MapView/>}/>
                                    <Route path="/contact" element={<Contact/>}/>
                                    <Route path="/about" element={<About/>}/>

                                    {/*Authentication Routes*/}
                                    <Route path="/login" element={<Login/>}/>
                                    <Route path="/register" element={<Register/>}/>
                                    <Route path="/forgot-password" element={<ForgotPassword/>}/>
                                    <Route path="/reset-password" element={<ResetPassword/>}/>
                                    <Route path="/verify-email/" element={<EmailVerification/>}/>
                                    <Route path="/verify-email/:token" element={<EmailVerification/>}/>

                                    {/*Property Manager Dashboard Routes*/}
                                    {/* ManagerDashboard was a mock-data scaffold; the real, API-backed
                                        property dashboard lives at /landlord/properties. */}
                                    <Route path="/manager" element={<Navigate to="/landlord/properties" replace/>}/>
                                    <Route path="/manager/properties" element={<Navigate to="/landlord/properties" replace/>}/>

                                    <Route path="/manager/properties/add" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <AddListing/>
                                        </RoleBasedRoute>
                                    }/>
                                    <Route path="/manager/properties/edit/:id" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <EditListing/>
                                        </RoleBasedRoute>
                                    }/>

                                    <Route path="/manager/messages" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <ManagerMessages/>
                                        </RoleBasedRoute>
                                    }/>

                                    <Route path="/manager/applications" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <ManagerApplications/>
                                        </RoleBasedRoute>
                                    }/>

                                    <Route path="/manager/analytics" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <ManagerAnalytics/>
                                        </RoleBasedRoute>
                                    }/>

                                    <Route path="/manager/profile" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <ManagerProfile/>
                                        </RoleBasedRoute>
                                    }/>
                                    <Route path="/manager/settings" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <ManagerSettings/>
                                        </RoleBasedRoute>
                                    }/>

                                    {/*Legal Pages*/}
                                    <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
                                    <Route path="/terms-of-service" element={<TermsOfService/>}/>

                                    <Route path="/rental-guide" element={<RentalGuide/>}/>
                                    <Route path="/moving-services" element={<MovingServices/>}/>
                                    <Route path="/property-management" element={<PropertyManagement/>}/>

                                    <Route path="/notifications" element={
                                        <ProtectedRoute>
                                            <UserNotifications />
                                        </ProtectedRoute>
                                    } />

                                    <Route path="/neighborhoods" element={<Neighborhoods />} />

                                    {/* Landlord Property Management Routes */}
                                    <Route path="/landlord/properties" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <PropertyDashboard />
                                        </RoleBasedRoute>
                                    } />
                                    <Route path="/landlord/properties/create" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <PropertyForm />
                                        </RoleBasedRoute>
                                    } />
                                    <Route path="/landlord/properties/edit/:propertyId" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <PropertyForm />
                                        </RoleBasedRoute>
                                    } />
                                    <Route path="/landlord/properties/:propertyId" element={
                                        <RoleBasedRoute allowedRoles={['manager', 'admin']}>
                                            <PropertyDetails />
                                        </RoleBasedRoute>
                                    } />

                                    {/* User Dashboard Routes */}
                                    <Route path="/dashboard" element={
                                        <ProtectedRoute>
                                            <UserDashboard />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/profile" element={
                                        <ProtectedRoute>
                                            <UserProfile />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/saved-properties" element={
                                        <ProtectedRoute>
                                            <SavedProperties />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/applications" element={
                                        <ProtectedRoute>
                                            <ApplicationHistory />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/messages" element={
                                        <ProtectedRoute>
                                            <UserMessages />
                                        </ProtectedRoute>
                                    } />
                                    {/* Settings now lives inside /profile - redirect anything still pointing here */}
                                    <Route path="/settings" element={<Navigate to="/profile" replace />} />

                                    <Route path="/apply/:propertyId" element={
                                        <ProtectedRoute>
                                            <PropertyApplication />
                                        </ProtectedRoute>
                                    } />

                                    <Route path="*" element={<PageNotFound />} />

                                    {/*
                                    <Route path="/neighborhood/:slug" element={<NeighborhoodDetail />} />
                                    <Route path="/rental-guide" element={<RentalGuide />} />

                                    <Route path="/faq" element={<FAQ />} />
                                    <Route path="/blog" element={<Blog />} />
                                    <Route path="/blog/:slug" element={<BlogPost />} />

                                     Application Process Routes
                                    <Route path="/apply/:propertyId" element={
                                        <ProtectedRoute>
                                            <PropertyApplication />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/application-status/:id" element={
                                        <ProtectedRoute>
                                            <ApplicationStatus />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/application-success" element={
                                        <ProtectedRoute>
                                            <ApplicationSuccess />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/background-check/:applicationId" element={
                                        <ProtectedRoute>
                                            <BackgroundCheck />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/documents/:applicationId" element={
                                        <ProtectedRoute>
                                            <DocumentUpload />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/lease/:applicationId" element={
                                        <ProtectedRoute>
                                            <LeaseAgreement />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/payment-setup/:applicationId" element={
                                        <ProtectedRoute>
                                            <PaymentSetup />
                                        </ProtectedRoute>
                                    } />

                                     User Dashboard Routes
                                    <Route path="/dashboard" element={
                                        <ProtectedRoute>
                                            <UserDashboard />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/profile" element={
                                        <ProtectedRoute>
                                            <UserProfile />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/saved-properties" element={
                                        <ProtectedRoute>
                                            <SavedProperties />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/applications" element={
                                        <ProtectedRoute>
                                            <ApplicationHistory />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/messages" element={
                                        <ProtectedRoute>
                                            <UserMessages />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/notifications" element={
                                        <ProtectedRoute>
                                            <UserNotifications />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/settings" element={
                                        <ProtectedRoute>
                                            <UserSettings />
                                        </ProtectedRoute>
                                    } />

                                     Admin Dashboard Routes
                                    <Route path="/admin" element={
                                        <RoleBasedRoute allowedRoles={['admin']}>
                                            <AdminDashboard />
                                        </RoleBasedRoute>
                                    } />
                                    <Route path="/admin/users" element={
                                        <RoleBasedRoute allowedRoles={['admin']}>
                                            <ManageUsers />
                                        </RoleBasedRoute>
                                    } />
                                    <Route path="/admin/managers" element={
                                        <RoleBasedRoute allowedRoles={['admin']}>
                                            <ManageManagers />
                                        </RoleBasedRoute>
                                    } />
                                    <Route path="/admin/analytics" element={
                                        <RoleBasedRoute allowedRoles={['admin']}>
                                            <SystemAnalytics />
                                        </RoleBasedRoute>
                                    } />
                                    <Route path="/admin/settings" element={
                                        <RoleBasedRoute allowedRoles={['admin']}>
                                            <PlatformSettings />
                                        </RoleBasedRoute>
                                    } />
                                    <Route path="/admin/content" element={
                                        <RoleBasedRoute allowedRoles={['admin']}>
                                            <ContentManagement />
                                        </RoleBasedRoute>
                                    } />
                                    <Route path="/admin/reports" element={
                                        <RoleBasedRoute allowedRoles={['admin']}>
                                            <ReportedContent />
                                        </RoleBasedRoute>
                                    } />
                                    <Route path="/admin/logs" element={
                                        <RoleBasedRoute allowedRoles={['admin']}>
                                            <SystemLogs />
                                        </RoleBasedRoute>
                                    } />

                                     Utility Routes
                                    <Route path="/coming-soon" element={<ComingSoon />} />
                                    <Route path="/server-error" element={<ServerError />} />

                                     Fallback Routes
                                    <Route path="/404" element={<PageNotFound />} />
                                    <Route path="*" element={<Navigate to="/404" replace />} />*/}
                                </Routes>
                            </main>

                            <Footer/>

                            {/* Global Components */}
                            <NotificationToast
                                notifications={notifications}
                                removeNotification={removeNotification}
                            />

                            <ScrollToTop variant="progress" position="bottom-right"/>
                        </Router>
                    </div>
                </NotificationContext.Provider>
            </PropertyContext.Provider>
        </AuthContext.Provider>
    );
}

export default App;